'use client'

import {
   Badge,
   Box,
   Button,
   Card,
   Container,
   Group,
   SimpleGrid,
   Stack,
   Text,
   ThemeIcon
} from '@blocks'
import {
   siteFeatureCardBackgroundHex,
   sitePrimaryDeepHex,
   siteSectionBodyHex,
   siteSectionHeadingHex,
   siteTypographyEyebrowFontSizeDesktop,
   siteTypographyEyebrowFontSizeMobile,
   siteTypographySectionLeadFontSizeDesktop,
   siteTypographySectionLeadFontSizeMobile,
   siteTypographySectionTitleDisplayFontSizeDesktop,
   siteTypographySectionTitleDisplayFontSizeMobile,
   siteTypographySectionTitlePrimaryFontSizeDesktop,
   siteTypographySectionTitlePrimaryFontSizeMobile,
   siteTypographySectionTitleSecondaryFontSizeDesktop,
   siteTypographySectionTitleSecondaryFontSizeMobile
} from '@constants'
import {useMediaQuery} from '@hooks'
import {
   IconArrowRight,
   IconCaravan,
   IconCarSuv,
   IconCheck,
   IconFileInvoice,
   IconSettings,
   IconSteeringWheel,
   IconTag,
   IconTool,
   IconTractor,
   IconTruck,
   IconTruckDelivery,
   IconUsers
} from '@icons'
import Image from 'next/image'
import {useRouter} from 'next/navigation'
import {useState} from 'react'

const accountBenefits = [
   'Have multiple vehicles linked under one account',
   'Issue a wash tag per vehicle',
   'Configure each tag to allow all or specific wash types',
   'Let drivers access self-serve, auto wash, or vacuums',
   'Keep things simple with monthly invoicing'
]

const vehicleTypes = [
   {icon: IconCarSuv, label: 'Company cars'},
   {icon: IconTruckDelivery, label: 'Tradie utes'},
   {icon: IconCaravan, label: 'Vans'},
   {icon: IconTool, label: 'Service vehicles'},
   {icon: IconTractor, label: 'Farm vehicles'},
   {icon: IconSteeringWheel, label: 'Daily-use work vehicles'}
]

const steps = [
   {
      number: '01',
      icon: IconUsers,
      title: 'Set up your fleet account',
      description:
         'We can set up your business with a fleet account and add as many vehicles as needed.'
   },
   {
      number: '02',
      icon: IconTag,
      title: 'Issue a tag for each vehicle',
      description:
         'Each approved vehicle receives its own wash tag. When setting up, you can choose to enable all wash options or restrict access to specific services — such as automatic wash only, self-serve bays, or vacuums.'
   },
   {
      number: '03',
      icon: IconTruck,
      title: 'Wash as needed',
      description:
         'Drivers use their assigned tag to access whichever services are enabled for that vehicle — self-serve bays, automatic wash, or vacuum stations.'
   },
   {
      number: '04',
      icon: IconFileInvoice,
      title: 'Receive one monthly invoice',
      description:
         'All usage is tracked under your fleet account and invoiced monthly for easy administration.'
   }
]

const whyReasons = [
   'Easy fleet account setup',
   'Individual tags issued per vehicle',
   'Configure tags to allow all or specific wash types',
   'Access to self-serve bays, automatic wash, and vacuums',
   'Multiple vehicles under one account',
   'Monthly invoicing for simple admin',
   'Suitable for a wide range of working vehicles',
   'Convenient local option in Morrinsville',
   'Locally owned and operated'
]

const customers = [
   {
      name: 'Kodra',
      logoUrl:
         'https://www.kodra.co.nz/assets/kodra-lockup-horizontal-light.svg',
      website: 'https://www.kodra.co.nz'
   },
   {
      name: 'Chemrite',
      logoUrl:
         'https://www.chemrite.co.nz/cdn/shop/files/Chemrite_Logo_ba863e1b-d34d-427e-877c-2bcc53265477.jpg?v=1773669671&width=300',
      website: 'https://www.chemrite.co.nz'
   },
   {
      name: 'The Top Pub & Eatery',
      logoUrl:
         'https://morrinsvillenz.co.nz/wp-content/uploads/2024/10/95198207_114199083604346_5571968275484508160_n.jpg',
      website: 'https://www.thetoppubeatery.co.nz'
   },
   {
      name: 'Top Hawk Builders',
      logoUrl: 'https://www.tophawk.builders/email-logo.svg',
      website: 'https://www.tophawk.builders'
   }
]

const FleetLogoCard = ({
   name,
   logoUrl,
   website
}: {
   name: string
   logoUrl: string
   website: string
}) => {
   const [hasError, setHasError] = useState(false)

   return (
      <a
         href={website}
         target={'_blank'}
         rel={'noopener noreferrer'}
         style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 48px',
            flexShrink: 0,
            userSelect: 'none',
            textDecoration: 'none',
            opacity: 1,
            transition: 'opacity 0.2s ease',
            cursor: 'pointer'
         }}
         onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '0.65'
         }}
         onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '1'
         }}
      >
         {!hasError ? (
            <Image
               src={logoUrl}
               alt={name}
               width={160}
               height={60}
               style={{
                  width: 'auto',
                  height: 56,
                  objectFit: 'contain'
               }}
               onError={() => setHasError(true)}
            />
         ) : (
            <Text
               c={siteSectionHeadingHex}
               fz={16}
               fw={700}
               ta={'center'}
               style={{whiteSpace: 'nowrap'}}
            >
               {name}
            </Text>
         )}
      </a>
   )
}

export const Fleet = () => {
   const router = useRouter()
   const isMobile = useMediaQuery('(max-width: 768px)')

   const goToContactFleetTopic = () => {
      router.push('/contact?topic=fleet')
   }

   return (
      <Stack gap={0}>
         {/* Hero / Intro */}
         <Box bg={'white'}>
            <Container
               py={isMobile ? 80 : 120}
               size={'xl'}
            >
               <SimpleGrid
                  cols={{base: 1, md: 2}}
                  spacing={isMobile ? 40 : 80}
                  style={{alignItems: 'center'}}
               >
                  <Stack gap={28}>
                     <Stack gap={14}>
                        <Badge
                           variant={'light'}
                           color={'blue'}
                           size={'lg'}
                           fw={700}
                           tt={'uppercase'}
                        >
                           Fleet Washing
                        </Badge>
                        <Text
                           c={siteSectionHeadingHex}
                           fz={
                              isMobile
                                 ? siteTypographySectionTitleDisplayFontSizeMobile
                                 : siteTypographySectionTitleDisplayFontSizeDesktop
                           }
                           fw={800}
                           lh={1.05}
                        >
                           Keep Your Fleet Clean. Keep It Simple.
                        </Text>
                     </Stack>
                     <Text
                        c={siteSectionBodyHex}
                        fz={
                           isMobile
                              ? siteTypographySectionLeadFontSizeMobile
                              : siteTypographySectionLeadFontSizeDesktop
                        }
                        fw={500}
                        lh={1.75}
                     >
                        Our fleet wash setup is designed to be
                        straightforward for businesses that need a
                        reliable way to keep vehicles clean and
                        presentable. It is a practical option for
                        businesses that want to avoid the hassle of
                        one-off payments and make regular vehicle
                        cleaning easier to manage.
                     </Text>
                  </Stack>

                  <Card
                     p={isMobile ? 30 : 44}
                     radius={'xl'}
                     style={{
                        background: siteFeatureCardBackgroundHex,
                        boxShadow: '0 12px 48px rgba(10,55,121,0.28)'
                     }}
                  >
                     <Stack gap={24}>
                        <Group
                           gap={14}
                           align={'center'}
                        >
                           <ThemeIcon
                              size={54}
                              radius={'lg'}
                              style={{
                                 background: 'rgba(255,255,255,0.15)',
                                 color: 'white'
                              }}
                           >
                              <IconUsers
                                 stroke={1.6}
                                 size={28}
                              />
                           </ThemeIcon>
                           <Text
                              c={'white'}
                              fz={
                                 isMobile
                                    ? siteTypographySectionLeadFontSizeMobile
                                    : siteTypographySectionLeadFontSizeDesktop
                              }
                              fw={700}
                           >
                              With a fleet account you can:
                           </Text>
                        </Group>
                        <Stack gap={14}>
                           {accountBenefits.map((benefit) => (
                              <Group
                                 key={benefit}
                                 gap={14}
                                 align={'flex-start'}
                                 wrap={'nowrap'}
                              >
                                 <IconCheck
                                    size={18}
                                    color={'rgba(255,255,255,0.7)'}
                                    style={{
                                       marginTop: 2,
                                       flexShrink: 0
                                    }}
                                 />
                                 <Text
                                    c={'white'}
                                    fz={
                                       isMobile
                                          ? siteTypographySectionLeadFontSizeMobile
                                          : siteTypographySectionLeadFontSizeDesktop
                                    }
                                    fw={500}
                                    lh={1.5}
                                 >
                                    {benefit}
                                 </Text>
                              </Group>
                           ))}
                        </Stack>
                     </Stack>
                  </Card>
               </SimpleGrid>
            </Container>
         </Box>

         {/* Built for Working Vehicles */}
         <Box
            style={{
               background: `linear-gradient(135deg, #061840 0%, ${siteSectionHeadingHex} 100%)`
            }}
         >
            <Container
               py={isMobile ? 80 : 120}
               size={'xl'}
            >
               <Stack gap={isMobile ? 40 : 60}>
                  <Stack
                     align={'center'}
                     gap={14}
                  >
                     <Text
                        c={'rgba(255,255,255,0.45)'}
                        fz={
                           isMobile
                              ? siteTypographyEyebrowFontSizeMobile
                              : siteTypographyEyebrowFontSizeDesktop
                        }
                        fw={700}
                        tt={'uppercase'}
                        ta={'center'}
                        lts={2.5}
                     >
                        Vehicle Types
                     </Text>
                     <Text
                        c={'white'}
                        fz={
                           isMobile
                              ? siteTypographySectionTitleSecondaryFontSizeMobile
                              : siteTypographySectionTitleSecondaryFontSizeDesktop
                        }
                        fw={800}
                        ta={'center'}
                        lh={1.1}
                     >
                        Built for Working Vehicles
                     </Text>
                     <Text
                        c={'rgba(255,255,255,0.65)'}
                        fz={
                           isMobile
                              ? siteTypographySectionLeadFontSizeMobile
                              : siteTypographySectionLeadFontSizeDesktop
                        }
                        fw={500}
                        ta={'center'}
                        maw={600}
                        lh={1.75}
                     >
                        We know local businesses rely on all sorts of
                        vehicles day to day. Clean Park caters to a
                        wide range of vehicles used around
                        Morrinsville, whether they are on the road all
                        day, out on-site, or moving between jobs.
                     </Text>
                  </Stack>

                  <style>{`
                     .fleet-vehicle-type-card {
                        transition:
                           background-color 0.2s ease,
                           border-color 0.2s ease,
                           transform 0.2s ease;
                     }
                     .fleet-vehicle-type-card:hover {
                        background-color: rgba(255, 255, 255, 0.12);
                        border-color: rgba(255, 255, 255, 0.22);
                        transform: translateY(-2px);
                     }
                  `}</style>
                  <SimpleGrid
                     cols={{base: 1, sm: 2, lg: 3}}
                     spacing={{base: 16, sm: 20}}
                  >
                     {vehicleTypes.map((vehicle) => {
                        const VehicleIcon = vehicle.icon
                        return (
                           <Card
                              key={vehicle.label}
                              className={'fleet-vehicle-type-card'}
                              p={isMobile ? 22 : 28}
                              radius={'xl'}
                              style={{
                                 background: 'rgba(255,255,255,0.08)',
                                 border:
                                    '1px solid rgba(255,255,255,0.14)',
                                 boxShadow:
                                    '0 8px 24px rgba(0, 0, 0, 0.12)'
                              }}
                           >
                              <Group
                                 gap={16}
                                 align={'flex-start'}
                                 wrap={'nowrap'}
                              >
                                 <ThemeIcon
                                    p={12}
                                    size={60}
                                    radius={'lg'}
                                    color={'white'}
                                    variant={'light'}
                                    style={{
                                       flexShrink: 0
                                    }}
                                 >
                                    <VehicleIcon
                                       stroke={1.6}
                                       size={32}
                                    />
                                 </ThemeIcon>
                                 <Text
                                    c={'white'}
                                    fz={
                                       isMobile
                                          ? siteTypographySectionLeadFontSizeMobile
                                          : siteTypographySectionLeadFontSizeDesktop
                                    }
                                    fw={700}
                                    ta={'left'}
                                    lh={1.4}
                                    style={{
                                       flex: 1,
                                       minWidth: 0,
                                       alignSelf: 'center'
                                    }}
                                 >
                                    {vehicle.label}
                                 </Text>
                              </Group>
                           </Card>
                        )
                     })}
                  </SimpleGrid>
               </Stack>
            </Container>
         </Box>

         {/* A Simple Process */}
         <Box bg={'white'}>
            <Container
               py={isMobile ? 80 : 120}
               size={'xl'}
            >
               <Stack gap={isMobile ? 40 : 60}>
                  <Stack
                     align={'center'}
                     gap={14}
                  >
                     <Badge
                        variant={'light'}
                        color={'blue'}
                        size={'lg'}
                        fw={700}
                        tt={'uppercase'}
                     >
                        How It Works
                     </Badge>
                     <Text
                        c={siteSectionHeadingHex}
                        fz={
                           isMobile
                              ? siteTypographySectionTitleSecondaryFontSizeMobile
                              : siteTypographySectionTitleSecondaryFontSizeDesktop
                        }
                        fw={800}
                        ta={'center'}
                        lh={1.1}
                     >
                        A Simple Process
                     </Text>
                     <Text
                        c={siteSectionBodyHex}
                        fz={
                           isMobile
                              ? siteTypographySectionLeadFontSizeMobile
                              : siteTypographySectionLeadFontSizeDesktop
                        }
                        fw={500}
                        ta={'center'}
                        maw={540}
                        lh={1.75}
                     >
                        Our fleet system is built to be easy for both
                        owners and drivers.
                     </Text>
                  </Stack>

                  <SimpleGrid
                     cols={{base: 1, sm: 2}}
                     spacing={20}
                  >
                     {steps.map((step) => (
                        <Card
                           key={step.number}
                           p={isMobile ? 28 : 40}
                           radius={'xl'}
                           bg={'white'}
                           style={{
                              boxShadow:
                                 '0 4px 28px rgba(10,55,121,0.09)',
                              border: '1px solid #e8ecf4'
                           }}
                        >
                           <Stack gap={18}>
                              <Group
                                 gap={16}
                                 align={'center'}
                              >
                                 <Text
                                    c={'rgba(10,55,121,0.18)'}
                                    fz={52}
                                    fw={900}
                                    lh={1}
                                    style={{
                                       fontVariantNumeric:
                                          'tabular-nums'
                                    }}
                                 >
                                    {step.number}
                                 </Text>
                                 <ThemeIcon
                                    size={50}
                                    radius={'lg'}
                                    color={siteSectionHeadingHex}
                                    variant={'light'}
                                 >
                                    <step.icon
                                       stroke={1.6}
                                       size={24}
                                    />
                                 </ThemeIcon>
                              </Group>
                              <Stack gap={8}>
                                 <Text
                                    c={siteSectionHeadingHex}
                                    fz={
                                       isMobile
                                          ? siteTypographySectionLeadFontSizeMobile
                                          : siteTypographySectionLeadFontSizeDesktop
                                    }
                                    fw={700}
                                 >
                                    {step.title}
                                 </Text>
                                 <Text
                                    c={siteSectionBodyHex}
                                    fz={
                                       isMobile
                                          ? siteTypographySectionLeadFontSizeMobile
                                          : siteTypographySectionLeadFontSizeDesktop
                                    }
                                    fw={500}
                                    lh={1.7}
                                 >
                                    {step.description}
                                 </Text>
                              </Stack>
                           </Stack>
                        </Card>
                     ))}
                  </SimpleGrid>

                  {/* Account limits callout */}
                  <Card
                     p={isMobile ? 28 : 40}
                     radius={'xl'}
                     style={{
                        background: '#f4f7ff',
                        border: '1.5px solid #dce5f5'
                     }}
                  >
                     <Group
                        gap={isMobile ? 16 : 28}
                        align={'flex-start'}
                        wrap={isMobile ? 'wrap' : 'nowrap'}
                     >
                        <ThemeIcon
                           size={56}
                           radius={'lg'}
                           color={siteSectionHeadingHex}
                           variant={'light'}
                           style={{flexShrink: 0}}
                        >
                           <IconSettings
                              stroke={1.6}
                              size={28}
                           />
                        </ThemeIcon>
                        <Stack gap={8}>
                           <Text
                              c={siteSectionHeadingHex}
                              fz={
                                 isMobile
                                    ? siteTypographySectionLeadFontSizeMobile
                                    : siteTypographySectionLeadFontSizeDesktop
                              }
                              fw={700}
                           >
                              Account Limits & Usage Controls
                           </Text>
                           <Text
                              c={siteSectionBodyHex}
                              fz={
                                 isMobile
                                    ? siteTypographySectionLeadFontSizeMobile
                                    : siteTypographySectionLeadFontSizeDesktop
                              }
                              fw={500}
                              lh={1.75}
                           >
                              You can also set limits at the account
                              level or per vehicle. This gives you
                              control over how much each vehicle uses
                              the service — whether that is a cap on
                              wash frequency, a spend limit per tag,
                              or both. A useful option for businesses
                              that want tighter oversight over fleet
                              running costs.
                           </Text>
                        </Stack>
                     </Group>
                  </Card>
               </Stack>
            </Container>
         </Box>

         {/* Fleet Customers Marquee */}
         <Box
            bg={'white'}
            py={isMobile ? 50 : 70}
            style={{overflow: 'hidden'}}
         >
            <style>{`
               @keyframes fleetMarquee {
                  from { transform: translateX(0); }
                  to   { transform: translateX(-50%); }
               }
               .fleet-marquee-track {
                  display: flex;
                  align-items: center;
                  width: max-content;
                  animation: fleetMarquee 24s linear infinite;
               }
               .fleet-marquee-track:hover {
                  animation-play-state: paused;
               }
            `}</style>

            <Stack gap={36}>
               <Stack
                  align={'center'}
                  gap={10}
               >
                  <Text
                     c={'rgba(10,55,121,0.4)'}
                     fz={
                        isMobile
                           ? siteTypographyEyebrowFontSizeMobile
                           : siteTypographyEyebrowFontSizeDesktop
                     }
                     fw={700}
                     tt={'uppercase'}
                     ta={'center'}
                     lts={2.5}
                  >
                     Fleet Customers
                  </Text>
                  <Text
                     c={siteSectionHeadingHex}
                     fz={
                        isMobile
                           ? siteTypographySectionTitleSecondaryFontSizeMobile
                           : siteTypographySectionTitleSecondaryFontSizeDesktop
                     }
                     fw={800}
                     ta={'center'}
                     lh={1.1}
                  >
                     Trusted by Local Businesses
                  </Text>
               </Stack>

               <div className={'fleet-marquee-track'}>
                  {[
                     ...customers,
                     ...customers,
                     ...customers,
                     ...customers
                  ].map((customer, index) => (
                     <FleetLogoCard
                        key={index}
                        name={customer.name}
                        logoUrl={customer.logoUrl}
                        website={customer.website}
                     />
                  ))}
               </div>
            </Stack>
         </Box>

         {/* Why Choose + CTA */}
         <Box
            style={{
               background: `linear-gradient(160deg, #061840 0%, ${siteSectionHeadingHex} 100%)`
            }}
         >
            <Container
               py={isMobile ? 80 : 120}
               size={'xl'}
            >
               <SimpleGrid
                  cols={{base: 1, md: 2}}
                  spacing={isMobile ? 50 : 80}
                  style={{alignItems: 'center'}}
               >
                  {/* Why reasons */}
                  <Stack gap={30}>
                     <Stack gap={14}>
                        <Badge
                           variant={'white'}
                           size={'lg'}
                           fw={700}
                           tt={'uppercase'}
                           c={siteSectionHeadingHex}
                        >
                           Why Clean Park
                        </Badge>
                        <Text
                           c={'white'}
                           fz={
                              isMobile
                                 ? siteTypographySectionTitlePrimaryFontSizeMobile
                                 : siteTypographySectionTitlePrimaryFontSizeDesktop
                           }
                           fw={800}
                           lh={1.1}
                        >
                           Why Use Clean Park for Fleet Washing
                        </Text>
                     </Stack>
                     <Stack gap={12}>
                        {whyReasons.map((reason) => (
                           <Group
                              key={reason}
                              gap={12}
                              align={'flex-start'}
                              wrap={'nowrap'}
                           >
                              <IconCheck
                                 size={18}
                                 color={'rgba(255,255,255,0.7)'}
                                 style={{marginTop: 2, flexShrink: 0}}
                              />
                              <Text
                                 c={'rgba(255,255,255,0.82)'}
                                 fz={
                                    isMobile
                                       ? siteTypographySectionLeadFontSizeMobile
                                       : siteTypographySectionLeadFontSizeDesktop
                                 }
                                 fw={500}
                                 lh={1.6}
                              >
                                 {reason}
                              </Text>
                           </Group>
                        ))}
                     </Stack>
                  </Stack>

                  {/* CTA card */}
                  <Card
                     p={isMobile ? 32 : 50}
                     radius={'xl'}
                     bg={'white'}
                     style={{
                        boxShadow: '0 12px 60px rgba(0,0,0,0.3)'
                     }}
                  >
                     <Stack gap={28}>
                        <Stack gap={14}>
                           <Badge
                              variant={'light'}
                              color={'blue'}
                              size={'md'}
                              fw={700}
                              tt={'uppercase'}
                           >
                              Local, Practical, Reliable
                           </Badge>
                           <Text
                              c={siteSectionHeadingHex}
                              fz={
                                 isMobile
                                    ? siteTypographySectionTitleSecondaryFontSizeMobile
                                    : siteTypographySectionTitleSecondaryFontSizeDesktop
                              }
                              fw={800}
                              lh={1.2}
                           >
                              Looking for a simple fleet wash solution
                              for your business?
                           </Text>
                        </Stack>
                        <Text
                           c={siteSectionBodyHex}
                           fz={
                              isMobile
                                 ? siteTypographySectionLeadFontSizeMobile
                                 : siteTypographySectionLeadFontSizeDesktop
                           }
                           fw={500}
                           lh={1.75}
                        >
                           At Clean Park, we understand that working
                           vehicles need a wash solution that is easy
                           to use and easy to manage. Our fleet
                           accounts are designed to give local
                           businesses a straightforward way to keep
                           vehicles clean without complicating the
                           process.
                        </Text>
                        <Text
                           c={siteSectionBodyHex}
                           fz={
                              isMobile
                                 ? siteTypographySectionLeadFontSizeMobile
                                 : siteTypographySectionLeadFontSizeDesktop
                           }
                           fw={500}
                           lh={1.75}
                        >
                           If your business runs multiple vehicles and
                           you want a simple wash setup with monthly
                           billing, Clean Park is ready to help.
                        </Text>
                        <Button
                           c={'white'}
                           fw={700}
                           color={'primary'}
                           size={'lg'}
                           radius={'xl'}
                           variant={'gradient'}
                           gradient={{
                              from: sitePrimaryDeepHex,
                              to: siteSectionHeadingHex,
                              deg: 145
                           }}
                           rightSection={<IconArrowRight size={18} />}
                           onClick={goToContactFleetTopic}
                           style={{alignSelf: 'flex-start'}}
                        >
                           Talk to Us About Your Fleet
                        </Button>
                     </Stack>
                  </Card>
               </SimpleGrid>
            </Container>
         </Box>
      </Stack>
   )
}
